import { getVideoIdentifier } from '../../rrwebUtils'
import { VideoStartEvent } from '../../types'
import { ACCEPTABLE_SLOP_MILLIS } from './videoPlayerUtils'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.BaseVideoPlayerListeners)

export type EventListener = (this: Element, ev: HTMLVideoElementEventMap[keyof HTMLVideoElementEventMap]) => void

/**
 * This file was created to try and reduce the size of BaseVideoPlayer, and to group some of the concerns related to buffering into one place.
 */
export class BaseVideoPlayerListeners {
  protected videoElement: HTMLVideoElement | undefined
  protected readonly videoStartEvent: VideoStartEvent
  private videoShown = false

  protected constructor(videoElement: HTMLVideoElement | undefined, videoStartEvent: VideoStartEvent) {
    this.videoElement = videoElement
    this.videoStartEvent = videoStartEvent

    // if this is not a placeholder, initialize buffering listeners
    if (this.videoElement) {
      this.initBufferingListeners()
    }
  }

  getVideoIdentifier(): string {
    return getVideoIdentifier(this.videoStartEvent)
  }

  public getVideoElement() {
    return this.videoElement
  }

  public isShown() {
    return this.videoShown
  }

  /**
   * This is a map where we track all event listeners added to the video elements.
   * @private
   */
  private eventListeners = new Map<keyof HTMLVideoElementEventMap, Set<EventListener>>()

  /**
   * When prebuffering video, we temporarily need to listeners removed so that we don't trigger buffering events during prebuffering.
   * @private
   */
  protected detachListeners() {
    if (!this.videoElement) {
      logger.error('detachListeners for', this.getVideoIdentifier(), 'failed - No video element found.')
      return
    }
    for (const entry of this.eventListeners.entries()) {
      const [type, listeners] = entry
      for (const listener of listeners) {
        this.videoElement?.removeEventListener(type, listener)
      }
    }
    logger.debug('detachListeners for', this.getVideoIdentifier(), 'completed')
  }

  /**
   * When a full snapshot is processed and the dom is rebuilt, all the videos are removed from the dom. To recover, we not only have to rettach the
   * videos, all the events listeners are dropped by the browser when it is removed from the dom, so we have to re-add them after reinserting
   * the video back into the dom.
   * @private
   */
  protected reAddEventListeners() {
    if (!this.videoElement) {
      logger.error('reAddEventListeners for', this.getVideoIdentifier(), 'failed - No video element found.')
      return
    }
    for (const entry of this.eventListeners.entries()) {
      const [type, listeners] = entry
      for (const listener of listeners) {
        this.videoElement?.removeEventListener(type, listener)
        this.videoElement?.addEventListener(type, listener)
      }
    }
    logger.debug('reAddEventListeners for ', this.getVideoIdentifier())
  }

  /**
   * Adds an event listener to the video element and tracks it in the eventListeners map.
   * @param type The event type to listen for.
   * @param listener The event listener function.
   * @param options
   */
  public addEventListener<K extends keyof HTMLVideoElementEventMap>(type: K, listener: EventListener, options?: AddEventListenerOptions): void {
    if (!this.videoElement) {
      logger.error('addEventListener for', this.getVideoIdentifier(), 'failed - No video element found.')
      return
    }

    this.videoElement.addEventListener(type, listener, options)

    if (!options?.once) {
      if (!this.eventListeners.has(type)) {
        this.eventListeners.set(type, new Set())
      }
      this.eventListeners.get(type)?.add(listener)
    }
  }

  /**
   * These listeners are a little complex because we only want to notify the replayer when the video is shown.
   * @private
   */
  private initBufferingListeners() {
    // waiting will fire when the video stops because it needs to buffer more data
    this.addEventListener('waiting', () => {
      logger.debug('video bufferingStart - currentTime:', this.videoElement?.currentTime, this.getVideoIdentifier())
      if (!this.buffering) {
        this.buffering = true
        if (this.videoShown) {
          // if we're entering a buffering state while the video is shown, notify listeners
          this.onBufferingStart()
        }
      }
    })

    // canplaythrough will fire when enough data has been buffered to play through to the end without interruption
    this.addEventListener('canplaythrough', () => {
      logger.debug('video canplaythrough event fired', this.getVideoIdentifier())
      if (this.buffering) {
        logger.debug('video bufferingEnd - currentTime:', this.videoElement?.currentTime, this.getVideoIdentifier())
        this.buffering = false
        if (this.videoShown) {
          this.onBufferingEnd()
        }
      }
    })
  }

  /**
   * If a video that we are waiting on changes from a shown to a hidden state, then we need to notify
   * the listeners that we no longer need to wait for the video, and vis/versa.
   * @param videoShown
   * @private
   */
  protected updateBufferingListeners(videoShown: boolean): void {
    logger.debug('updateBufferingListeners - videoShown:', videoShown, 'buffering:', this.buffering)
    const videoShownBefore = this.videoShown
    this.videoShown = videoShown
    if (this.buffering) {
      if (!videoShown && videoShownBefore) {
        this.onBufferingEnd()
      } else if (videoShown && !videoShownBefore) {
        this.onBufferingStart()
      }
    }
  }

  private buffering = false

  public isBuffering(): boolean {
    return this.buffering
  }

  private bufferingTimeout: ReturnType<typeof setTimeout> | undefined

  private onBufferingStart() {
    logger.debug('onBufferingStart:', this.getVideoIdentifier())

    /* Wait to see if the buffering ends quickly, and cancel the timeout if it does. 
       this slight delay was added to prevent a buffering loop when playing/resuming the replayer.
       If the scrubber position of an embedded video is updated right before calling play, then it goes into a buffering state momentarily even 
       if there is no need to buffer anything. When we sync the video state before in this particular case, we need it not to set currentTime 
       before calling play
     */
    this.bufferingTimeout = setTimeout(() => {
      this.bufferingStartListener?.()
      this.bufferingTimeout = undefined
    }, ACCEPTABLE_SLOP_MILLIS)
  }

  private onBufferingEnd() {
    logger.debug('onBufferingEnd:', this.getVideoIdentifier())
    if (this.bufferingTimeout) {
      clearTimeout(this.bufferingTimeout)
      this.bufferingTimeout = undefined
    } else {
      this.bufferingEndListener?.()
    }
  }

  private bufferingStartListener: (() => void) | null = null
  private bufferingEndListener: (() => void) | null = null

  public registerBufferingStartListener(cb: () => void): void {
    this.bufferingStartListener = cb
  }
  public registerBufferingEndListener(cb: () => void): void {
    this.bufferingEndListener = cb
  }
}
